import {useEffect, useMemo} from 'react';
import 'leaflet/dist/leaflet.css';
import {MapContainer, TileLayer, Marker, Popup, useMap} from 'react-leaflet';
import L from 'leaflet';
import {TrackingRoute} from 'types/tracking';
import {Address} from 'types/order';
import styles from './TrackingMap.module.scss';
import './TrackingMap.css';

type Props = {
  route: TrackingRoute;
  deliveryAddress: Address;
};

type DriverLocation = {
  lat: number;
  lng: number;
};

type DeliveryLocation = {
  lat: number;
  lng: number;
  address: string;
};

function FitBounds({
  driverLocation,
  deliveryLocation,
}: {
  driverLocation: DriverLocation;
  deliveryLocation: DeliveryLocation;
}) {
  const map = useMap();

  useEffect(() => {
    const points: L.LatLngExpression[] = [
      [driverLocation.lat, driverLocation.lng],
      [deliveryLocation.lat, deliveryLocation.lng],
    ];

    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, {padding: [50, 50]});
  }, [map, driverLocation, deliveryLocation]);

  return null;
}

const getCustomerMarkerIcon = (): L.DivIcon => {
  return new L.DivIcon({
    className: 'leaflet-div-icon',
    html: `<div class="${styles.marker} ${styles.inProgress} ${styles.current}">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z" fill="white"/>
      </svg>
    </div>`,
    iconSize: [36, 46],
    iconAnchor: [18, 46],
  });
};

const getDriverIcon = (): L.DivIcon => {
  return new L.DivIcon({
    className: 'leaflet-div-icon',
    html: `<div class="driver-marker">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5H15V3H9V5H6.5C5.84 5 5.28 5.42 5.08 6.01L3 12V20C3 20.55 3.45 21 4 21H5C5.55 21 6 20.55 6 20V19H18V20C18 20.55 18.45 21 19 21H20C20.55 21 21 20.55 21 20V12L18.92 6.01ZM6.5 16C5.67 16 5 15.33 5 14.5C5 13.67 5.67 13 6.5 13C7.33 13 8 13.67 8 14.5C8 15.33 7.33 16 6.5 16ZM17.5 16C16.67 16 16 15.33 16 14.5C16 13.67 16.67 13 17.5 13C18.33 13 19 13.67 19 14.5C19 15.33 18.33 16 17.5 16ZM5 11L6.5 6.5H17.5L19 11H5Z" fill="white"/>
      </svg>
    </div>`,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
  });
};

function TrackingMap({route, deliveryAddress}: Props) {
  const driverLocation: DriverLocation = useMemo(
    () => ({
      lat: route.driver.latitude,
      lng: route.driver.longitude,
    }),
    [route.driver.latitude, route.driver.longitude]
  );

  const deliveryLocation: DeliveryLocation = useMemo(
    () => ({
      lat: deliveryAddress.latitude,
      lng: deliveryAddress.longitude,
      address: `${deliveryAddress.address_line_1}, ${deliveryAddress.city}, ${deliveryAddress.state} ${deliveryAddress.zipcode}`,
    }),
    [deliveryAddress]
  );

  const center: L.LatLngExpression = useMemo(() => {
    return [
      (driverLocation.lat + deliveryLocation.lat) / 2,
      (driverLocation.lng + deliveryLocation.lng) / 2,
    ];
  }, [driverLocation, deliveryLocation]);

  return (
    <div className={styles.mapContainer}>
      <MapContainer
        center={center}
        style={{height: '100%', width: '100%'}}
        zoom={13}
        zoomControl={false}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a>'
        />

        <FitBounds
          driverLocation={driverLocation}
          deliveryLocation={deliveryLocation}
        />

        <Marker
          position={[driverLocation.lat, driverLocation.lng]}
          icon={getDriverIcon()}
        >
          <Popup>
            <div className={styles.popup}>
              <strong>Driver Location</strong>
              <p>Your delivery is on the way!</p>
            </div>
          </Popup>
        </Marker>

        <Marker
          position={[deliveryLocation.lat, deliveryLocation.lng]}
          icon={getCustomerMarkerIcon()}
        >
          <Popup>
            <div className={styles.popup}>
              <strong>Your Delivery</strong>
              <p>{deliveryLocation.address}</p>
            </div>
          </Popup>
        </Marker>
      </MapContainer>
    </div>
  );
}

export default TrackingMap;
